import { Component } from '@angular/core';
import { QueryService } from '@pt/api-client';
import { ActionsSubject, Store } from '@ngrx/store';
import { AbstractWidgetDirective } from '../../abstract-widget.directive';
import { FormGroup } from '@angular/forms';
import { REQUIRED_VALIDATOR_KEY, UiWidget, WidgetConfiguration, WidgetValidator, WTextViewMode } from '@pt/pt-models';
import { TranslateService } from '@ngx-translate/core';
import { WidgetDialogService } from '../../../services/widget-dialog.service';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
  selector: 'pt-widget-text-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">
      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [label]='widget?.label'
        [help]='widget?.help'
        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'

        (clickSaveWidget)='saveWidget()'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <ng-container *ngIf="internalStatus.showOptButton; else noOptional">

      </ng-container>

      <ng-template #noOptional>
        <pt-widget-text-v0-autosave
          *ngIf="internalStatus?.isAutoSave; else noAutosave"
          [label]="widget.label"
          [disabled]="internalStatus.viewMode !== 'EDIT'"
          [note]='currentValue'
          [noDataLabel]="noDataLabel"
          (changeNoteAndSave)="changeCurrentValueAndSave($event)"
        ></pt-widget-text-v0-autosave>

        <ng-template #noAutosave>

          <ng-container *ngIf='!internalStatus.showOptButton'>

            <pt-widget-input-note
              [ngClass]="{'d-none' : internalStatus.viewMode === 'PREVIEW'}"
              [dialogTitle]="widget.label"
              [viewMode]="viewMode"
              [note]='currentValue'
              [editable]="internalStatus.viewMode ==='EDIT'"
              (changeNote)='changeCurrentValue($event)'
              (closeEditor)="closeEditor(widget)"
              (changeNoteAndSave)="changeCurrentValueAndSave($event)">
            </pt-widget-input-note>

            <pt-widget-text-v0-preview
              *ngIf="internalStatus.viewMode === 'PREVIEW'"
              [showNoData]='true'
              [noDataLabel]="noDataLabel"
              [previewData]='previewData'
              [loading]='internalStatus.isLoading'>
            </pt-widget-text-v0-preview>

            <pt-widget-footer></pt-widget-footer>
          </ng-container>

        </ng-template>
      </ng-template>

    </div>
  `,
  styles: [`
    .text-v0-form {
      margin: 15px 8px 8px 8px;
      display: block;
    }

    .text-v0-form textarea {
      font-size: 12px;
    }
  `]
})
export class WidgetTextV0Component extends AbstractWidgetDirective<string> {
  public formGroup: FormGroup;
  currentValue: string;
  viewMode: WTextViewMode = WTextViewMode.DIALOG;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    this.currentValue = widget.value;
    this.viewMode = widget.properties?.textViewMode ? widget.properties?.textViewMode : WTextViewMode.DIALOG;
    this.checkValidity();
    setTimeout(() => this.dispatchCompleteState(widget.value));
  }

  saveWidget(ignoreTrigger?: boolean): void {
    console.log('saveWidget(ignoreTrigger?: boolean)');
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = this.currentValue;
    widget.comment = this.comment;
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  changeCurrentValue($event: string | undefined) {
    console.log('changeCurrentValue($event: string | undefined)');
    if (JSON.stringify(this.currentValue) !== JSON.stringify($event)) {
      this.currentValue = $event;
      this.checkValidity();
      if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.internalStatus.isDraft) {
        this.saveWidget();
      }
    }
  }

  changeCurrentValueAndSave($event: string | undefined) {
    console.log('changeCurrentValueAndSave($event: string | undefined)');
    if (JSON.stringify(this.currentValue) !== JSON.stringify($event)) {
      this.currentValue = $event;
      this.checkValidity();
    }
    this.saveWidget();
  }

  configuration(): WidgetConfiguration {
    return { supportAutoSave: true, supportValidation: false };
  }

  private checkValidity() {
    let valid = true;
    if (this.widget?.opt) {
      valid = true;
    } else {
      if (this.widget?.validators) {
        this.widget?.validators.forEach((v: WidgetValidator) => {
          switch (v.key) {
            case REQUIRED_VALIDATOR_KEY:
              if (!this.currentValue || this.currentValue.length === 0) {
                valid = false;
              }
              break;
            default:
              this.sendWidgetInvalidValidation(v);
          }
        });
      }
    }
    this.isValid$.next(valid);
  }
}
